import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Ship, ShieldCheck, ArrowRight, MessageSquare, Box, FileCheck } from 'lucide-react';
import { generateGeneralWhatsAppLink } from '../data/products';
import { PRODUCT_IMAGES } from '../data/images';
import LookbookGallery from '../components/LookbookGallery';

const Export = () => {
  const exportFeatures = [
    {
      icon: <Ship size={24} />,
      title: "GLOBAL PORT DISPATCH",
      desc: "Full Container Load (FCL) and Less than Container Load (LCL) shipments dispatched directly to major ports in Europe, Middle East, Americas, and Asia."
    },
    {
      icon: <FileCheck size={24} />,
      title: "INTERNATIONAL COMPLIANCE",
      desc: "Complete export documentation including Certificate of Origin, EUR.1 / Form A, SGS / Intertek inspection certifications, and HS Code 6203.42 compliance."
    },
    {
      icon: <Box size={24} />,
      title: "EXPORT-GRADE PACKAGING",
      desc: "Moisture-resistant poly bags, heavy-duty 7-ply export cartons, barcode tagging, and custom palletization for safe transatlantic transport."
    },
    {
      icon: <ShieldCheck size={24} />,
      title: "STRICT QC STANDARDS",
      desc: "AQL 2.5 international quality control audits, seam tension tests, color fastness checks, and shrinkage control before container loading."
    }
  ];

  const targetPorts = [
    { region: "EUROPE", ports: "Rotterdam, Hamburg, Felixstowe, Genoa, Antwerp" },
    { region: "MIDDLE EAST & NORTH AFRICA", ports: "Jebel Ali (Dubai), Jeddah, Dammam, Hamad (Qatar), Alexandria" },
    { region: "AMERICAS", ports: "New York/New Jersey, Los Angeles, Houston, Santos, Veracruz" },
    { region: "ASIA PACIFIC & OCEANIA", ports: "Singapore, Sydney, Melbourne, Busan, Yokohama" }
  ];

  return (
    <div style={{ paddingTop: 'calc(var(--nav-height) + 0px)', paddingBottom: '100px' }}>
      
      {/* Hero Banner */}
      <section style={{ position: 'relative', width: '100%', height: 'clamp(280px, 42vw, 500px)', overflow: 'hidden' }}>
        <img
          src={PRODUCT_IMAGES[0]}
          alt="RAYYAN DENIM Global Export Network"
          fetchpriority="high"
          loading="eager"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(15,23,42,0.3) 0%, rgba(15,23,42,0.85) 100%)' }} />
        <div style={{ position: 'absolute', bottom: '40px', left: 0, right: 0 }}>
          <div className="container">
            <span className="section-badge" style={{ background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.4)', color: '#ffffff' }}>
              <Globe size={13} />
              WORLDWIDE DENIM EXPORTER
            </span>
            <h1 style={{ fontSize: 'clamp(32px, 5.5vw, 64px)', fontFamily: 'var(--font-heading)', fontWeight: 900, textTransform: 'uppercase', color: '#ffffff', marginTop: '10px' }}>
              INTERNATIONAL <span style={{ color: 'var(--accent-magenta)' }}>EXPORT NETWORK</span>
            </h1>
            <p style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', color: 'rgba(255,255,255,0.85)', maxWidth: '720px', lineHeight: '1.5', marginTop: '8px' }}>
              Delivering high-specification <span className="brand-name">RAYYAN DENIM</span> collections directly to international importers, overseas fashion retail chains, and global distributors across 40+ countries.
            </p>
          </div>
        </div>
      </section>

      {/* Export Capabilities Grid */}
      <section style={{ padding: '80px 0', background: '#f8fafc', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '740px', margin: '0 auto 56px' }}>
            <span className="section-badge">WORLD CLASS STANDARDS</span>
            <h2 className="section-title">
              EXPORT <span className="gradient-text">CAPABILITIES & LOGISTICS</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              Engineered for seamless international trade, complete documentation compliance, and secure container shipments.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '28px' }}>
            {exportFeatures.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="glass-panel card-hover-effect"
                style={{ padding: '32px 24px', borderRadius: '16px' }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(224, 0, 135, 0.08)',
                    color: 'var(--accent-magenta)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}
                >
                  {item.icon}
                </div>
                <h3 style={{ fontSize: '17px', fontFamily: 'var(--font-heading)', fontWeight: 800, color: 'var(--text-main)', marginBottom: '10px' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: '13.5px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Ports Destination Section */}
      <section style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '740px', margin: '0 auto 48px' }}>
            <span className="section-badge">WORLDWIDE REACH</span>
            <h2 className="section-title">
              MAJOR INTERNATIONAL <span className="gradient-text">PORT DESTINATIONS</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              We regularly dispatch ocean cargo consignments to key global trade hubs.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {targetPorts.map((item, idx) => (
              <div
                key={idx}
                className="glass-panel"
                style={{
                  padding: '28px 24px',
                  borderRadius: '14px',
                  borderTop: '3px solid var(--accent-magenta)'
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 900, color: 'var(--accent-magenta)', letterSpacing: '0.08em', marginBottom: '8px' }}>
                  {item.region}
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-main)', lineHeight: '1.5' }}>
                  {item.ports}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Catalog Showcase */}
      <LookbookGallery />

      {/* Export Inquiry Banner */}
      <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#ffffff' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 44px)', fontFamily: 'var(--font-heading)', fontWeight: 900, textTransform: 'uppercase', marginBottom: '16px', color: '#ffffff' }}>
            START YOUR INTERNATIONAL <span style={{ color: 'var(--accent-magenta)' }}>EXPORT ENQUIRY</span>
          </h2>
          <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.8)', margin: '0 auto 32px', lineHeight: '1.6' }}>
            Speak directly with our International Export Desk for price lists, container lead times, sample dispatches, and custom denim development.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={generateGeneralWhatsAppLink("Export Desk Direct Enquiry")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              style={{ padding: '16px 32px', fontSize: '13px' }}
            >
              <MessageSquare size={18} />
              DISCUSS EXPORT ORDER ON WHATSAPP
            </a>
            <a
              href="/contact"
              className="btn-primary"
              style={{ padding: '16px 32px', fontSize: '13px', background: '#ffffff', color: '#0f172a' }}
            >
              <span>SUBMIT EXPORT ENQUIRY</span>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Export;
